import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Botao } from '../../components/Botao'
import { AvisoDeErro, EstadoVazio } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { IconeCheque } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import { useConfirmarCandidata, useDescartarCandidata } from '../../api/queries/iad'
import type { CandidataDoItem16 } from '../../types/iad'
import { DialogoDeTexto } from '../chamados/fiscalizacao/DialogoDeTexto'
import { dataDoInstante } from './pontuacao'

/** Que decisão está aberta e sobre qual solicitação. */
type Aberto = { acao: 'confirmar' | 'descartar'; gmudId: number } | null

/**
 * A conciliação do item 16: solicitações que chegaram à execução sem aprovação do comitê.
 *
 * **O sistema aponta; quem decide é você.** Nenhum ponto é lançado por esta lista — os dois botões
 * exigem um texto escrito, e é esse texto que a contratada vai ler. Dez pontos são um terço do
 * caminho até um desconto de 7% sobre a fatura inteira, e um número desses não pode nascer de uma
 * consulta.
 *
 * **A lista aparece para quem só consulta; os dois botões, não.** Uma solicitação que chegou à
 * produção sem passar pelo comitê é o que o item 16 pune, e esconder a lista de quem confere o
 * processo esconderia o achado. Decidir sobre ela é ato do fiscal.
 *
 * @param props o mês em apuração, as candidatas encontradas e se a conta decide sobre elas
 * @returns a lista de candidatas
 */
export function CandidatasDoItem16({
  competencia,
  candidatas,
  podeAgir,
}: {
  competencia: string
  candidatas: CandidataDoItem16[]
  podeAgir: boolean
}) {
  const t = TEXTOS.iad
  const [aberto, setAberto] = useState<Aberto>(null)
  const confirmar = useConfirmarCandidata()
  const descartar = useDescartarCandidata()
  const erro = confirmar.error ?? descartar.error

  return (
    <section className="space-y-3" aria-label={TEXTOS.iad.confirmar}>
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-semibold text-slate-900">Solicitações para conferir</h2>
        <Explicacao termo="conciliacaoDoItem16" />
      </div>

      {/* Confirmar imputa dez pontos e descartar tira a pergunta da frente para sempre: os dois
          precisam dizer quando não aconteceram. Antes, a falha sumia com o diálogo. */}
      {erro && <AvisoDeErro erro={erro} titulo={TEXTOS.fiscalizacao.acoes.naoDeuCerto} />}

      {candidatas.length === 0 ? (
        <EstadoVazio
          icone={<IconeCheque className="h-9 w-9" />}
          titulo={t.semCandidatas.titulo}
          ajuda={t.semCandidatas.ajuda}
        />
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
          {candidatas.map((c) => (
            <Candidata
              key={c.gmudId}
              candidata={c}
              podeAgir={podeAgir}
              aoAgir={(acao) => setAberto({ acao, gmudId: c.gmudId })}
            />
          ))}
        </ul>
      )}

      {aberto?.acao === 'confirmar' && (
        <DialogoDeTexto
          titulo={t.confirmar}
          ajuda={t.confirmarAjuda}
          rotulo="O que você apurou"
          aoConfirmar={(texto) => {
            confirmar.mutate({ competencia, gmudId: aberto.gmudId, texto })
            setAberto(null)
          }}
          aoFechar={() => setAberto(null)}
        />
      )}

      {aberto?.acao === 'descartar' && (
        <DialogoDeTexto
          titulo={t.descartar}
          ajuda={t.descartarAjuda}
          rotulo="Por que não é infração"
          aoConfirmar={(motivo) => {
            descartar.mutate({ competencia, gmudId: aberto.gmudId, motivo })
            setAberto(null)
          }}
          aoFechar={() => setAberto(null)}
        />
      )}
    </section>
  )
}

/** Uma solicitação sem registro de aprovação: o que se sabe dela e as duas saídas. */
function Candidata({
  candidata,
  podeAgir,
  aoAgir,
}: {
  candidata: CandidataDoItem16
  podeAgir: boolean
  aoAgir: (acao: 'confirmar' | 'descartar') => void
}) {
  const t = TEXTOS.iad

  return (
    <li className="space-y-2 px-3 py-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Link to={`/gmuds/${candidata.gmudId}`} className="font-medium text-sky-800 hover:underline">
          {candidata.codigo}
        </Link>
        <span className="text-slate-600">{candidata.sistema}</span>
        {candidata.chamadoNumero && <span className="text-slate-500">· {candidata.chamadoNumero}</span>}
      </div>

      <p className="text-xs text-slate-600">
        {candidata.dataImplementacaoExecutada
          ? t.implantadaEm(dataDoInstante(candidata.dataImplementacaoExecutada))
          : t.entrouEmExecucao(dataDoInstante(candidata.entrouEmExecucaoEm))}
      </p>

      {candidata.aprovadaForaDaAplicacao && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{t.foraDaAplicacao}</p>
      )}

      {podeAgir && (
        <div className="flex flex-wrap gap-2">
          <Botao aparencia="primario" onClick={() => aoAgir('confirmar')}>
            {t.confirmar}
          </Botao>
          <Botao onClick={() => aoAgir('descartar')}>{t.descartar}</Botao>
        </div>
      )}
    </li>
  )
}
